import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { aiGovernanceGov001Api, type Gov001Status, type SprintDashboard, type SprintValidation } from '../../api/ai-governance-gov001';

type Tab = 'overview' | 'modules' | 'sources' | 'providers' | 'jobs' | 'gates' | 'status';

export function AIGovernanceGov001Page() {
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<SprintDashboard | null>(null);
  const [modules, setModules] = useState<{ key: string; name: string; category: string }[]>([]);
  const [sources, setSources] = useState<{ key: string; name: string; category: string }[]>([]);
  const [providers, setProviders] = useState<{ key: string; name: string; interface: string }[]>([]);
  const [jobs, setJobs] = useState<{ key: string; name: string; schedule?: string }[]>([]);
  const [gates, setGates] = useState<{ key: string; name: string; blocking: boolean }[]>([]);
  const [status, setStatus] = useState<Gov001Status | null>(null);
  const [validation, setValidation] = useState<SprintValidation | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const card = 'border-indigo-200 bg-white dark:border-indigo-900/40 dark:bg-zinc-900';
  const muted = 'text-slate-500 dark:text-zinc-400';
  const accent = 'text-indigo-700 dark:text-indigo-300';

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, mod, src, prov, jb, gt, st] = await Promise.all([
        aiGovernanceGov001Api.dashboard(), aiGovernanceGov001Api.governanceModules(),
        aiGovernanceGov001Api.traceabilitySources(), aiGovernanceGov001Api.providers(),
        aiGovernanceGov001Api.backgroundJobs(), aiGovernanceGov001Api.qualityGates(),
        aiGovernanceGov001Api.status(),
      ]);
      setDashboard(dash); setModules(mod); setSources(src); setProviders(prov); setJobs(jb); setGates(gt); setStatus(st);
    } catch (e) { setError(e instanceof Error ? e.message : 'Failed to load sprint center'); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const validate = async () => {
    setBusy(true); setActionMsg(null);
    try {
      const result = await aiGovernanceGov001Api.validate({
        every_recommendation_explainable: true, complete_lineage: true,
        evidence_complete: true, governance_approval: true,
        risk_assessment_complete: true, transparency_coverage_complete: true,
      });
      setValidation(result);
      setActionMsg(result.valid ? 'AI Governance passes all release gates' : `Gaps: ${result.gaps?.join(', ')}`);
    } catch (e) { setError(e instanceof Error ? e.message : 'Validation failed'); }
    finally { setBusy(false); }
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Overview' }, { id: 'modules', label: 'Governance Modules' },
    { id: 'sources', label: 'Traceability Sources' }, { id: 'providers', label: 'Providers' },
    { id: 'jobs', label: 'Jobs' }, { id: 'gates', label: 'Quality Gates' }, { id: 'status', label: 'Status' },
  ];

  return (
    <div className="space-y-6 rounded-xl bg-indigo-50/20 p-1 dark:bg-zinc-950">
      <div>
        <p className={`text-xs font-semibold uppercase ${accent}`}>GOV-000001 · P0 · Mandatory Before Pilot</p>
        <h1 className="text-2xl font-semibold">Enterprise AI Governance & Explainability Platform</h1>
        <p className={`text-sm ${muted}`}>Transparent, traceable, explainable, measurable and auditable — trust through evidence, not claims.</p>
        <div className="mt-2 flex flex-wrap gap-2 text-xs">
          <Link to="/platform/autonomous-ops-aow001" className={`${accent} hover:underline`}>AOW-000001</Link>
          <span className={muted}>→</span>
          <Link to="/ai-governance" className={`${accent} hover:underline`}>Live Trust Command Center</Link>
        </div>
      </div>

      {error && <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {actionMsg && <div className="rounded-lg border border-indigo-300 bg-indigo-50 p-3 text-sm text-indigo-700">{actionMsg}</div>}

      <div className="flex flex-wrap gap-2">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)} className={`rounded-lg px-3 py-1.5 text-sm ${tab === t.id ? 'bg-indigo-600 text-white' : `${card} border`}`}>{t.label}</button>
        ))}
      </div>

      {tab === 'overview' && dashboard && (
        <div className="space-y-4">
          <div className={`rounded-xl border p-5 ${card}`}>
            <h2 className="text-lg font-semibold">Never Ask Customers to Blindly Trust AI</h2>
            <p className={`mt-2 text-sm ${muted}`}>Decision traceability, explainability engine, evidence graph, counterfactual analysis, certification and full audit trail for every recommendation.</p>
          </div>
          <div className="grid gap-3 sm:grid-cols-4">
            {[{ label: 'Objectives', value: dashboard.objectives }, { label: 'Governance Modules', value: dashboard.governance_modules },
              { label: 'Traceability Sources', value: dashboard.traceability_sources }, { label: 'Quality Gates', value: dashboard.quality_gates }].map((m) => (
              <div key={m.label} className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>{m.label}</p><p className="text-2xl font-semibold">{m.value}</p></div>
            ))}
          </div>
          <button type="button" disabled={busy} onClick={validate} className="rounded-lg bg-indigo-600 px-4 py-2 text-sm text-white disabled:opacity-50">Validate Release Gates</button>
          {validation && <p className={`text-sm ${validation.valid ? 'text-indigo-600' : 'text-red-600'}`}>{validation.valid ? 'All gates passed' : validation.gaps?.join(', ')}</p>}
        </div>
      )}

      {tab === 'modules' && modules.map((m) => (
        <div key={m.key} className={`rounded-xl border p-4 ${card}`}><p className="font-semibold">{m.name}</p><p className={`text-xs ${muted}`}>{m.category}</p></div>
      ))}

      {tab === 'sources' && sources.map((s) => (
        <div key={s.key} className={`rounded-xl border p-4 ${card}`}><p className="font-semibold">{s.name}</p><p className={`text-xs ${muted}`}>{s.category}</p></div>
      ))}

      {tab === 'providers' && providers.map((p) => (
        <div key={p.key} className={`rounded-xl border p-4 ${card}`}><p className="font-semibold">{p.name}</p><p className={`text-xs ${muted}`}>{p.interface}</p></div>
      ))}

      {tab === 'jobs' && jobs.map((j) => (
        <div key={j.key} className={`rounded-xl border p-4 ${card}`}><p className="font-semibold">{j.name}</p><p className={`text-xs ${muted}`}>{j.schedule ?? 'on-demand'}</p></div>
      ))}

      {tab === 'gates' && gates.map((g) => (
        <div key={g.key} className={`rounded-xl border p-4 ${card}`}><p className="font-semibold">{g.name}</p><p className={`text-xs ${muted}`}>{g.blocking ? 'Blocking' : 'Advisory'}</p></div>
      ))}

      {tab === 'status' && status && (
        <div className={`rounded-xl border p-4 ${card}`}>
          <p className="font-semibold">Sprint {status.sprint_id}</p>
          <p className={`text-sm ${muted}`}>{status.objectives} objectives · {status.governance_modules} modules · {status.traceability_sources} traceability sources</p>
          <p className={`text-sm ${accent}`}>Explainability · Evidence graph · Certification · Risk assessment · Transparency</p>
        </div>
      )}
    </div>
  );
}
